/**
 * Retail DELUXE Fall 2026 landing — hero clear-window interaction.
 * Scoped to [data-rdv7-stage]; cleans up on Shopify section unload/reload.
 */
(function () {
  'use strict';

  var instances = new WeakMap();
  var blockedPersonalEmailDomains = {
    'gmail.com': true,
    'googlemail.com': true,
  };
  var workEmailValidationMessage = 'Please register with your professional email address.';
  var registrationReceivedPath = '/pages/registration-received';
  var registrationRedirectDelayMs = 2000;

  function getEmailDomain(value) {
    var normalizedValue = String(value || '').trim().toLowerCase();
    var atIndex = normalizedValue.lastIndexOf('@');
    return atIndex === -1 ? '' : normalizedValue.slice(atIndex + 1);
  }

  function maskURI(w, h, pad, r, blur) {
    var svg =
      "<svg xmlns='http://www.w3.org/2000/svg' width='" +
      (w + pad * 2) +
      "' height='" +
      (h + pad * 2) +
      "'><defs><filter id='b' x='-30%' y='-30%' width='160%' height='160%'><feGaussianBlur stdDeviation='" +
      blur +
      "'/></filter></defs><rect x='" +
      pad +
      "' y='" +
      pad +
      "' width='" +
      w +
      "' height='" +
      h +
      "' rx='" +
      r +
      "' fill='white' filter='url(%23b)'/></svg>";
    return 'url("data:image/svg+xml;charset=utf-8,' + svg.replace(/#/g, '%23') + '")';
  }

  function initStage(stage) {
    if (!stage || instances.has(stage)) return;

    var hero = stage.querySelector('[data-rdv7-hero]');
    var landing = stage.closest('.rdv7-landing');
    var fogImg = stage.querySelector('[data-rdv7-fog]');
    var sharp = stage.querySelector('[data-rdv7-sharp]');
    var frame = stage.querySelector('[data-rdv7-frame]');

    function reuseFogSource(image) {
      if (!image || !fogImg) return;

      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('fetchpriority');

      function syncSource() {
        if (!fogImg.currentSrc) return false;
        image.src = fogImg.currentSrc;
        return true;
      }

      if (!syncSource()) {
        fogImg.addEventListener('load', syncSource, { once: true });
      }
    }

    if (!sharp && fogImg) {
      sharp = fogImg.cloneNode(false);
      sharp.className = 'rdv7-layer rdv7-sharp';
      sharp.removeAttribute('data-rdv7-fog');
      sharp.setAttribute('data-rdv7-sharp', '');
      sharp.setAttribute('alt', '');
      sharp.setAttribute('aria-hidden', 'true');
      reuseFogSource(sharp);

      if (frame && frame.parentNode) {
        frame.parentNode.insertBefore(sharp, frame);
      } else {
        var fogPicture = fogImg.parentNode;
        if (fogPicture && fogPicture.parentNode) {
          fogPicture.parentNode.insertBefore(sharp, fogPicture.nextSibling);
        }
      }
    }
    var lineFog = stage.querySelector('[data-rdv7-line-fog]');
    var lineClear = stage.querySelector('[data-rdv7-line-clear]');
    var grab = stage.querySelector('[data-rdv7-grab]');
    var panel = stage.querySelector('[data-rdv7-form]');
    var formEmbed = panel && panel.querySelector('[data-rdv7-form-embed]');
    var formIframe = formEmbed && formEmbed.querySelector('.rdv7-form-iframe');
    var nativeForm = formEmbed && formEmbed.querySelector('[data-rdv7-native-form]');
    var nativeEmailInput = nativeForm && nativeForm.querySelector('input[type="email"]');
    var nativeUrlInputs = nativeForm ? nativeForm.querySelectorAll('input[type="url"]') : [];
    var nativeFormFields = nativeForm && nativeForm.querySelector('[data-rdv7-form-fields]');
    var nativeFormSuccess = nativeForm && nativeForm.querySelector('[data-rdv7-form-success]');
    var nativeFormError = nativeForm && nativeForm.querySelector('[data-rdv7-form-error]');
    var nativeFormSubmit = nativeForm && nativeForm.querySelector('[data-rdv7-form-submit]');
    var nativeFormTitle = nativeForm && formEmbed.querySelector('.rdv7-hf-title');
    var nativeFormUrgency = nativeForm && formEmbed.querySelector('.rdv7-hf-urgent');
    var nativeFormInvite = nativeForm && panel.querySelector('.rdv7-hf-invite');

    if (!hero || !frame || !grab || !panel) return;
    /* fog/sharp require Shopify Files image_picker uploads; skip window FX if absent */

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var mqOverlay = window.matchMedia('(min-width: 901px)');

    var IMG_W = 2400;
    var IMG_H = 1792;
    var EYES = { x: 1210, y: 560 };
    var LEYE = { x: 850, y: 560 };
    var REYE = { x: 1570, y: 560 };
    var OBJ_POS = { x: 0.5, y: 0.22 };
    var ZOOM = 1.15;

    var WIN = { w: 520, h: 180, pad: 60 };
    var pos = { x: 0, y: 0 };
    var dragged = false;
    var dragging = false;
    var off = { x: 0, y: 0 };
    var F = 0;
    var G = { s: 1, ox: 0, oy: 0 };
    var mobileFocusY = null;
    var mobileOverlap = 0;
    var fogExtension = null;
    var introPlayed = false;
    var introRunning = false;
    var glide = null;
    var timers = [];
    var rafIds = [];
    var resizeObserver = null;
    var iframeMutationObserver = null;
    var formRevealed = false;
    var formReadySignal = false;
    var layoutPending = false;
    var sheetRevealReady = false;
    var introRevealComplete = false;
    var mobileViewportHeight = Math.max(600, Math.round(window.innerHeight));

    var FORM_INSET_TOP_MIN = 112;

    function mobileViewportH() {
      return mobileViewportHeight || Math.max(600, Math.round(window.innerHeight));
    }

    function mobileFormGap() {
      return window.innerWidth > mobileViewportH() ? 32 : 24;
    }

    function syncMobileSheetVars() {
      if (mqOverlay.matches || !hero || !panel) return;
      mobileOverlap = Math.round(mobileViewportH() * 0.5);
      stage.style.setProperty('--rdv7-hero-overlap', mobileOverlap + 'px');
    }

    function syncEmbedHeight() {
      if (!formEmbed || !formIframe) return 0;

      /*
       * The embed script writes the form's measured content height inline.
       * Read that before offsetHeight, which is still clamped by the larger
       * loading/skeleton min-height.
       */
      var iframeHeight = Math.ceil(parseFloat(formIframe.style.height));
      if (!iframeHeight) {
        iframeHeight =
          Math.ceil(formIframe.offsetHeight) ||
          parseInt(formIframe.getAttribute('data-height'), 10) ||
          0;
      }
      if (iframeHeight > 0) {
        var heightValue = iframeHeight + 'px';
        if (formIframe.style.minHeight) {
          formIframe.style.removeProperty('min-height');
        }
        if (formEmbed.style.getPropertyValue('--rdv7-embed-h') !== heightValue) {
          formEmbed.style.setProperty('--rdv7-embed-h', heightValue);
        }
        if (formEmbed.style.height !== heightValue) {
          formEmbed.style.height = heightValue;
        }
        if (formEmbed.style.minHeight) {
          formEmbed.style.removeProperty('min-height');
        }
      }

      return iframeHeight;
    }

    function syncFormLayout() {
      if (!panel || !hero || !stage) return;

      syncEmbedHeight();
      var panelHeight = panel.offsetHeight;

      if (mqOverlay.matches) {
        var baseHero = Math.max(600, window.innerHeight);
        var neededHeight = panelHeight + FORM_INSET_TOP_MIN * 2;
        var finalHeight = Math.max(baseHero, neededHeight);
        var top = Math.max(FORM_INSET_TOP_MIN, (finalHeight - panelHeight) / 2);

        panel.style.setProperty('--rdv7-form-offset-top', Math.round(top) + 'px');
        panel.style.removeProperty('--rdv7-form-margin-top');
        panel.style.removeProperty('--rdv7-form-margin-bottom');
        panel.style.transform = 'none';

        hero.style.removeProperty('--rdv7-hero-need');
        hero.style.removeProperty('--rdv7-hero-extra');
        hero.style.setProperty(
          '--rdv7-hero-extension',
          Math.max(0, Math.round(finalHeight - window.innerHeight)) + 'px'
        );
        hero.style.setProperty('--rdv7-hero-h', Math.round(finalHeight) + 'px');
        hero.style.minHeight = Math.round(finalHeight) + 'px';
        hero.style.height = 'auto';
        stage.style.minHeight = '';
        return;
      }

      panel.style.removeProperty('--rdv7-form-offset-top');
      panel.style.removeProperty('transform');
      hero.style.removeProperty('--rdv7-hero-h');
      hero.style.minHeight = '';
      hero.style.height = '';

      syncMobileSheetVars();

      var viewportHeight = mobileViewportH();
      var overlap = mobileOverlap || Math.round(viewportHeight * 0.5);
      var bottomGhost = hero.querySelector('.rdv7-ghost--bottom');
      var ghostHeight = bottomGhost ? bottomGhost.offsetHeight : 0;
      var heroNeed = Math.max(
        viewportHeight,
        Math.round(
          overlap +
            panelHeight +
            20 +
            ghostHeight +
            viewportHeight * 0.014 +
            16
        )
      );
      var formMarginBottom = heroNeed - overlap - panelHeight;

      hero.style.setProperty('--rdv7-hero-need', heroNeed + 'px');
      hero.style.setProperty(
        '--rdv7-hero-extension',
        Math.max(0, Math.round(heroNeed - viewportHeight)) + 'px'
      );
      hero.style.setProperty(
        '--rdv7-hero-extra',
        (stage.classList.contains('sheet-up') ? 0 : heroNeed - viewportHeight) + 'px'
      );
      panel.style.setProperty(
        '--rdv7-form-margin-top',
        '-' + (heroNeed - overlap) + 'px'
      );
      panel.style.setProperty(
        '--rdv7-form-margin-bottom',
        Math.max(0, formMarginBottom) + 'px'
      );
      stage.style.minHeight = '';
    }

    function syncMobileFormToWindow() {
      if (mqOverlay.matches || !hero || !panel || !frame || !grab) return;

      /*
       * Reserve against the window's final resting position, not its current
       * intro-animation coordinates. ResizeObserver can run while the window
       * glides between the eyes; reading the animated grab/label rectangles
       * here made the form follow that motion by roughly 18px.
       */
      var finalCenterY = eyesScreen().y;
      finalCenterY = Math.max(minimumWindowCenterY(8), finalCenterY);
      var contentBottom = finalCenterY + WIN.h / 2 + 34;

      if (lineClear) {
        contentBottom = Math.max(
          contentBottom,
          finalCenterY + WIN.h / 2 + 10 + lineClear.offsetHeight
        );
      }

      var formTop = Math.ceil(contentBottom + mobileFormGap());
      var panelHeight = panel.offsetHeight;
      var viewportHeight = mobileViewportH();
      var bottomGhost = hero.querySelector('.rdv7-ghost--bottom');
      var ghostHeight = bottomGhost ? bottomGhost.offsetHeight : 0;
      var trailingSpace = Math.round(
        20 + ghostHeight + viewportHeight * 0.014 + 16
      );
      var heroNeed = Math.max(
        viewportHeight,
        formTop + panelHeight + trailingSpace
      );
      var formMarginBottom = heroNeed - formTop - panelHeight;

      hero.style.setProperty('--rdv7-hero-need', Math.round(heroNeed) + 'px');
      hero.style.setProperty(
        '--rdv7-hero-extension',
        Math.max(0, Math.round(heroNeed - viewportHeight)) + 'px'
      );
      hero.style.setProperty(
        '--rdv7-hero-extra',
        (stage.classList.contains('sheet-up') ? 0 : heroNeed - viewportHeight) + 'px'
      );
      panel.style.setProperty(
        '--rdv7-form-margin-top',
        '-' + Math.max(0, Math.round(heroNeed - formTop)) + 'px'
      );
      panel.style.setProperty(
        '--rdv7-form-margin-bottom',
        Math.max(0, Math.round(formMarginBottom)) + 'px'
      );
    }

    function scheduleLayout() {
      if (layoutPending) return;
      layoutPending = true;
      trackRaf(function () {
        layoutPending = false;
        layout();
      });
    }

    function trackTimeout(fn, ms) {
      var id = window.setTimeout(fn, ms);
      timers.push(id);
      return id;
    }

    function trackRaf(fn) {
      var id = window.requestAnimationFrame(fn);
      rafIds.push(id);
      return id;
    }

    function onFormMessage(event) {
      if (!formIframe || typeof event.data !== 'string') return;
      if (event.data.indexOf('[iFrameSizer]') !== 0) return;
      if (event.data.indexOf(formIframe.id) === -1) return;
      /*
       * The initial inline iframe height only reserves space; it does not mean
       * the remote form has rendered. iFrameSizer posts after GHL has measured
       * its rendered content, so keep the skeleton until this real ready signal.
       */
      formReadySignal = true;
      trackRaf(revealMeasuredForm);
    }

    function onIframeLoad() {
      /*
       * The iframe stays visible from first paint with the skeleton layered
       * behind it. Its load event can precede GHL's client-rendered fields, so
       * only the later iFrameSizer signal removes the skeleton.
       */
      scheduleLayout();
    }

    function onWindowLoad() {
      layout();
    }

    function revealMeasuredForm() {
      if (!formIframe || !formReadySignal) return;
      var measuredHeight = parseFloat(formIframe.style.height);
      if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return;

      /*
       * GHL can publish one or more temporary heights while its styles and
       * fonts load. The iframe can paint progressively above the skeleton;
       * once the first reliable measurement arrives, remove the skeleton and
       * continue syncing every content-height update normally.
       */
      if (formRevealed) {
        syncEmbedHeight();
        scheduleLayout();
        return;
      }

      revealForm();
    }

    function revealForm() {
      if (!formEmbed || formRevealed) return;
      formRevealed = true;
      syncEmbedHeight();
      formEmbed.classList.add('is-loaded');
      layout();
      trackRaf(function () {
        hero.classList.add('form-ready');
      });
    }

    function onNativeFormSubmit(event) {
      if (!nativeForm || !nativeFormSubmit) return;
      event.preventDefault();
      if (nativeFormSubmit.disabled) return;
      var nextEvent = nativeForm.hasAttribute('data-rdv7-next-event-form');
      var endpoint = nativeForm.action;
      if (nextEvent) {
        endpoint = nativeForm.getAttribute('data-next-event-endpoint');
        try {
          var destination = new URL(endpoint);
          var localPreview = destination.protocol === 'http:' &&
            ['127.0.0.1', 'localhost'].indexOf(window.location.hostname) !== -1 &&
            destination.origin === window.location.origin;
          if ((!localPreview && destination.protocol !== 'https:') || destination.pathname !== '/next-event') throw new Error('Invalid destination');
        } catch (error) {
          if (nativeFormError) {
            nativeFormError.textContent = 'Registration for the next event is not available yet. Please check back soon.';
            nativeFormError.hidden = false;
          }
          return;
        }
      }

      normalizeNativeUrlInputs();
      validateWorkEmail();
      if (!nativeForm.reportValidity()) return;
      if (nativeFormError) nativeFormError.hidden = true;
      nativeFormSubmit.disabled = true;
      nativeForm.setAttribute('aria-busy', 'true');
      var submitLabel = nativeFormSubmit.querySelector('span');
      var originalLabel = submitLabel && submitLabel.textContent;
      if (nextEvent && submitLabel) submitLabel.textContent = 'Submitting…';
      var submitAbort = nextEvent ? new AbortController() : null;
      var submitTimeout = submitAbort ? window.setTimeout(function () { submitAbort.abort(); }, 45000) : null;

      (nextEvent ? window.RDV7NextEvent.submit(nativeForm, submitAbort.signal) : window.fetch(endpoint, {
        method: 'POST', body: new FormData(nativeForm), headers: { Accept: 'application/json' }, credentials: 'same-origin',
      }).then(function (response) {
        if (!response.ok) throw new Error('Registration request failed');
        return response;
      }))
        .then(function () {
          panel.style.minHeight = Math.ceil(panel.getBoundingClientRect().height) + 'px';
          panel.classList.add('is-registration-success');
          nativeForm.reset();
          if (nativeFormFields) nativeFormFields.hidden = true;
          if (nativeFormTitle) nativeFormTitle.hidden = true;
          if (nativeFormUrgency) nativeFormUrgency.hidden = true;
          if (nativeFormInvite) nativeFormInvite.hidden = true;
          if (nativeFormSuccess) {
            nativeFormSuccess.hidden = false;
            nativeFormSuccess.focus({ preventScroll: true });
          }
          scheduleLayout();
          if (!nextEvent) trackTimeout(function () {
            window.location.assign(registrationReceivedPath);
          }, registrationRedirectDelayMs);
        })
        .catch(function () {
          if (nativeFormError) {
            if (nextEvent) nativeFormError.textContent = 'We couldn’t save your registration. Please try again shortly.';
            nativeFormError.hidden = false;
          }
        })
        .then(function () {
          nativeFormSubmit.disabled = false;
          if (submitTimeout) window.clearTimeout(submitTimeout);
          if (nextEvent && submitLabel) submitLabel.textContent = originalLabel;
          nativeForm.removeAttribute('aria-busy');
          scheduleLayout();
        });
    }

    function validateWorkEmail() {
      if (!nativeEmailInput) return true;
      if (nativeForm.hasAttribute('data-rdv7-next-event-form')) {
        nativeEmailInput.value = nativeEmailInput.value.trim();
        var validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nativeEmailInput.value);
        nativeEmailInput.setCustomValidity(validEmail || !nativeEmailInput.value ? '' : 'Please enter a valid email address.');
        return validEmail;
      }
      var isBlocked = Boolean(blockedPersonalEmailDomains[getEmailDomain(nativeEmailInput.value)]);
      nativeEmailInput.setCustomValidity(isBlocked ? workEmailValidationMessage : '');
      return !isBlocked;
    }

    function onNativeEmailInput() {
      validateWorkEmail();
    }

    function normalizeNativeUrlInput(input) {
      var value = input.value.trim();
      if (/^www\.[^\s]+/i.test(value)) input.value = 'https://' + value;
    }

    function normalizeNativeUrlInputs() {
      nativeUrlInputs.forEach(function (input) {
        normalizeNativeUrlInput(input);
      });
    }

    function onNativeUrlInput(event) {
      normalizeNativeUrlInput(event.currentTarget);
    }

    function freeW() {
      if (!mqOverlay.matches) return hero.clientWidth;
      var hr = hero.getBoundingClientRect();
      var pr = panel.getBoundingClientRect();
      return Math.max(320, pr.left - hr.left - 18);
    }

    function sizeWindow() {
      var fluidMax = Math.min(572, Math.max(420, Math.round(window.innerWidth * 0.298)));
      WIN.w = Math.min(fluidMax, Math.round(F * 0.86));
      WIN.h = Math.round(WIN.w * 0.318);
      WIN.pad = Math.round(Math.max(44, WIN.w * 0.115));
      if (!sharp) return;
      sharp.style.setProperty(
        '--mask-url',
        maskURI(WIN.w, WIN.h, WIN.pad, Math.round(WIN.h * 0.12), 13)
      );
      sharp.style.setProperty('--mw', WIN.w + WIN.pad * 2 + 'px');
      sharp.style.setProperty('--mh', WIN.h + WIN.pad * 2 + 'px');
    }

    function coverGeom() {
      var w = hero.clientWidth;
      var h = hero.clientHeight;
      var s0 = Math.max(w / IMG_W, h / IMG_H);

      if (!mqOverlay.matches) {
        /*
         * Match the updated reference banner on mobile: keep the left eye at
         * 42.4% of the viewport width and 36% of the first viewport height.
         * Clamp against that stable viewport as well: the hero grows when GHL
         * reports its final height, but the image crop must not move with it.
         */
        var stableMobileViewportHeight = mobileViewportH();
        var mobileEyeX = w * 0.424;
        var mobileEyeY = Math.round(stableMobileViewportHeight * 0.36);
        var mobileScale = Math.max(
          w / IMG_W,
          0.47 * (w / 390),
          mobileEyeY / LEYE.y
        );
        var mobileLeft = mobileEyeX - LEYE.x * mobileScale;
        var mobileTop = mobileEyeY - LEYE.y * mobileScale;
        mobileLeft = Math.min(0, Math.max(w - IMG_W * mobileScale, mobileLeft));
        mobileTop = Math.min(
          0,
          Math.max(stableMobileViewportHeight - IMG_H * mobileScale, mobileTop)
        );
        return { s: mobileScale, ox: mobileLeft, oy: mobileTop };
      }

      var oy0 = (h - IMG_H * s0) * OBJ_POS.y;
      var ey = oy0 + EYES.y * s0;
      var ex = F / 2;
      var s = s0 * ZOOM;
      var ox = ex - EYES.x * s;
      var oy = ey - EYES.y * s;
      ox = Math.min(0, Math.max(w - IMG_W * s, ox));
      oy = Math.min(0, Math.max(h - IMG_H * s, oy));
      return { s: s, ox: ox, oy: oy };
    }

    function applyGeom() {
      var g = coverGeom();
      G = g;
      [fogImg, sharp].forEach(function (el) {
        if (!el) return;
        el.style.width = IMG_W * g.s + 'px';
        el.style.height = IMG_H * g.s + 'px';
        el.style.left = g.ox + 'px';
        el.style.top = g.oy + 'px';
      });

      if (!fogImg) return;
      var imageBottom = g.oy + IMG_H * g.s;
      if (!mqOverlay.matches && hero.clientHeight > imageBottom - 2) {
        if (!fogExtension) {
          fogExtension = fogImg.cloneNode();
          fogExtension.removeAttribute('data-rdv7-fog');
          fogExtension.classList.add('rdv7-fog-extension');
          fogExtension.setAttribute('aria-hidden', 'true');
          reuseFogSource(fogExtension);
          var fogContainer = fogImg.parentNode;
          if (fogContainer && fogContainer.tagName === 'PICTURE') {
            fogContainer.parentNode.insertBefore(fogExtension, fogContainer.nextSibling);
          } else if (fogContainer) {
            fogContainer.insertBefore(fogExtension, fogImg.nextSibling);
          }
        }
        fogExtension.style.width = IMG_W * g.s + 'px';
        fogExtension.style.height = IMG_H * g.s + 'px';
        fogExtension.style.left = g.ox + 'px';
        positionFogExtension(imageBottom);
        fogExtension.style.transform = 'scale(1.09, -1.09)';
        fogExtension.style.display = '';
      } else if (fogExtension) {
        fogExtension.style.display = 'none';
      }
    }

    function positionFogExtension(imageBottom) {
      if (!fogExtension) return;
      var blend = Math.round(Math.min(180, Math.max(120, mobileViewportH() * 0.18)));
      fogExtension.style.top = Math.round(imageBottom - blend) + 'px';
      fogExtension.style.setProperty('--rdv7-fog-extension-blend', blend + 'px');
    }

    function toScreen(pt) {
      return { x: G.ox + pt.x * G.s, y: G.oy + pt.y * G.s };
    }

    function eyesScreen() {
      var eye = toScreen(LEYE);
      if (!mqOverlay.matches) {
        eye.x = F / 2;
        if (mobileFocusY !== null) eye.y = mobileFocusY;
      }
      return eye;
    }

    function hasShortMobileViewport() {
      return !mqOverlay.matches && window.innerHeight < 600;
    }

    function minimumWindowCenterY(m) {
      var minCenterY = WIN.h / 2 + m;

      if (
        hasShortMobileViewport() &&
        lineFog
      ) {
        var masthead = hero.querySelector('.rdv7-masthead');
        if (masthead) {
          var heroRect = hero.getBoundingClientRect();
          var mastBottom = masthead.getBoundingClientRect().bottom - heroRect.top;
          minCenterY = Math.max(
            minCenterY,
            mastBottom + 24 + lineFog.offsetHeight + 10 + WIN.h / 2
          );
        }
      }

      return minCenterY;
    }

    function alignMobileEye() {
      mobileFocusY = null;
      if (mqOverlay.matches) return;

      var eye = eyesScreen();
      var targetY = eye.y;
      if (hasShortMobileViewport()) {
        targetY = Math.max(targetY, minimumWindowCenterY(8));
      }
      var renderedEyeY = eye.y;

      if (fogImg) {
        var heroRect = hero.getBoundingClientRect();
        var imageRect = fogImg.getBoundingClientRect();
        renderedEyeY =
          imageRect.top -
          heroRect.top +
          (LEYE.y / IMG_H) * imageRect.height;
      }

      var shiftY = Math.max(0, targetY - renderedEyeY);
      mobileFocusY = renderedEyeY + shiftY;
      if (!shiftY) return;

      G.oy += shiftY;
      [fogImg, sharp].forEach(function (el) {
        if (el) el.style.top = G.oy + 'px';
      });

      if (fogExtension && fogExtension.style.display !== 'none') {
        positionFogExtension(G.oy + IMG_H * G.s);
      }
    }

    function clamp() {
      var m = 8;
      var bottomY = hero.clientHeight;
      var minCenterY = minimumWindowCenterY(m);

      pos.x = Math.min(F - WIN.w / 2 - m, Math.max(WIN.w / 2 + m, pos.x));
      pos.y = Math.min(bottomY - WIN.h / 2 - m, Math.max(minCenterY, pos.y));
    }

    function render() {
      if (sharp) {
        sharp.style.setProperty('--mx', pos.x - G.ox - WIN.w / 2 - WIN.pad + 'px');
        sharp.style.setProperty('--my', pos.y - G.oy - WIN.h / 2 - WIN.pad + 'px');
      }
      frame.style.transform =
        'translate3d(' +
        (pos.x - WIN.w / 2) +
        'px,' +
        (pos.y - WIN.h / 2) +
        'px,0)';
      frame.style.width = WIN.w + 'px';
      frame.style.height = WIN.h + 'px';

      if (lineFog) {
        lineFog.style.transform =
          'translate3d(' +
          (pos.x - WIN.w / 2) +
          'px,' +
          (pos.y - WIN.h / 2 - lineFog.offsetHeight - 10) +
          'px,0)';
      }
      if (lineClear) {
        lineClear.style.transform =
          'translate3d(' +
          (pos.x + WIN.w / 2 - lineClear.offsetWidth) +
          'px,' +
          (pos.y + WIN.h / 2 + 10) +
          'px,0)';
      }

      grab.style.transform =
        'translate3d(' +
        (pos.x - WIN.w / 2 - 34) +
        'px,' +
        (pos.y - WIN.h / 2 - 34) +
        'px,0)';
      grab.style.width = WIN.w + 68 + 'px';
      grab.style.height = WIN.h + 68 + 'px';
    }

    function home() {
      var e = eyesScreen();
      pos.x = e.x;
      pos.y = e.y;
      clamp();
      render();
    }

    function glideTo(tx, ty, mark) {
      if (mark !== false) dragged = true;
      var from = { x: pos.x, y: pos.y };
      pos.x = tx;
      pos.y = ty;
      clamp();
      var to = { x: pos.x, y: pos.y };
      pos.x = from.x;
      pos.y = from.y;
      if (reduced) {
        pos.x = to.x;
        pos.y = to.y;
        render();
        return;
      }
      if (glide) cancelAnimationFrame(glide);
      var t0 = performance.now();
      var D = 520;
      var ease = function (t) {
        return 1 - Math.pow(1 - t, 3);
      };
      (function step(now) {
        var k = Math.min(1, (now - t0) / D);
        var e = ease(k);
        pos.x = from.x + (to.x - from.x) * e;
        pos.y = from.y + (to.y - from.y) * e;
        render();
        if (k < 1 && !dragging) glide = requestAnimationFrame(step);
      })(t0);
    }

    function intro() {
      if (introPlayed) return;
      introPlayed = true;
      if (reduced) {
        home();
        return;
      }
      introRunning = true;
      var r = toScreen(REYE);
      pos.x = r.x;
      pos.y = r.y;
      clamp();
      render();
      trackTimeout(function () {
        if (dragged) {
          introRunning = false;
          return;
        }
        var l = eyesScreen();
        glideTo(l.x, l.y, false);
        trackTimeout(function () {
          introRunning = false;
        }, 560);
      }, 900);
    }

    function layout() {
      syncFormLayout();
      F = freeW();
      applyGeom();
      sizeWindow();
      alignMobileEye();
      if (introRunning) {
        clamp();
        render();
        syncMobileFormToWindow();
        return;
      }
      if (!dragged) home();
      else {
        clamp();
        render();
      }
      syncMobileFormToWindow();
    }

    function inWindow(x, y) {
      return Math.abs(x - pos.x) < WIN.w / 2 + 34 && Math.abs(y - pos.y) < WIN.h / 2 + 34;
    }

    function pt(ev) {
      var r = hero.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    }

    function onPointerDown(ev) {
      if (!mqOverlay.matches) return;
      var p = pt(ev);
      if (!inWindow(p.x, p.y)) return;
      dragging = true;
      dragged = true;
      off.x = p.x - pos.x;
      off.y = p.y - pos.y;
      grab.setPointerCapture(ev.pointerId);
      grab.style.cursor = 'grabbing';
      ev.preventDefault();
    }

    function onPointerMove(ev) {
      if (!dragging) return;
      var p = pt(ev);
      pos.x = p.x - off.x;
      pos.y = p.y - off.y;
      clamp();
      render();
    }

    function onPointerUp() {
      dragging = false;
      grab.style.cursor = 'grab';
    }

    function onHeroClick(ev) {
      if (ev.target.closest('.rdv7-masthead, .rdv7-hero-form, button, a, input, label, iframe')) return;
      if (!mqOverlay.matches) return;
      if (ev.target.closest('[data-rdv7-grab]')) return;
      var p = pt(ev);
      if (inWindow(p.x, p.y)) return;
      glideTo(p.x, p.y);
    }

    function onResize() {
      /*
       * Refresh the stable layout viewport when the browser/device viewport is
       * actually resized. Without this, mobile geometry keeps the height from
       * the previous responsive size until the page is reloaded.
       */
      if (!mqOverlay.matches) {
        mobileViewportHeight = Math.max(600, Math.round(window.innerHeight));
      }
      syncMobileSheetVars();
      scheduleLayout();
    }

    function onVisualViewportResize() {
      /*
       * Keyboard and browser-chrome changes affect visualViewport but should
       * not replace the stable layout height used by the mobile composition.
       */
      syncMobileSheetVars();
      scheduleLayout();
    }

    function onOrientationChange() {
      trackRaf(function () {
        trackRaf(function () {
          mobileViewportHeight = Math.max(600, Math.round(window.innerHeight));
          syncMobileSheetVars();
          layout();
        });
      });
    }

    function onMqChange() {
      if (mqOverlay.matches) {
        stage.classList.remove('sheet-up');
        stage.style.removeProperty('--rdv7-hero-overlap');
        mobileOverlap = 0;
        finishIntroReveal();
      } else {
        mobileViewportHeight = Math.max(600, Math.round(window.innerHeight));
        syncMobileSheetVars();
        if (sheetRevealReady || reduced) {
          stage.classList.add('sheet-up');
        } else {
          stage.classList.remove('sheet-up');
        }
      }
      layout();
    }

    function revealMobileSheet() {
      sheetRevealReady = true;
      if (mqOverlay.matches) {
        finishIntroReveal();
        return;
      }
      syncMobileSheetVars();
      stage.classList.add('sheet-up');
      trackRaf(layout);
      if (reduced) {
        finishIntroReveal();
      } else {
        trackTimeout(finishIntroReveal, 950);
      }
    }

    function finishIntroReveal() {
      if (introRevealComplete) return;
      introRevealComplete = true;
      if (!landing) return;
      landing.classList.remove('rdv7-intro-pending');
      landing.classList.add('rdv7-intro-complete');
    }

    function onFormTransitionEnd(event) {
      if (
        event.target === panel &&
        event.propertyName === 'transform' &&
        stage.classList.contains('sheet-up')
      ) {
        finishIntroReveal();
      }
    }

    grab.addEventListener('pointerdown', onPointerDown);
    grab.addEventListener('pointermove', onPointerMove);
    grab.addEventListener('pointerup', onPointerUp);
    grab.addEventListener('pointercancel', onPointerUp);
    hero.addEventListener('click', onHeroClick);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);
    window.addEventListener('load', onWindowLoad);
    window.addEventListener('message', onFormMessage);
    panel.addEventListener('transitionend', onFormTransitionEnd);
    if (formIframe) formIframe.addEventListener('load', onIframeLoad);
    if (nativeForm) nativeForm.addEventListener('submit', onNativeFormSubmit);
    if (nativeEmailInput) nativeEmailInput.addEventListener('input', onNativeEmailInput);
    nativeUrlInputs.forEach(function (input) {
      input.addEventListener('input', onNativeUrlInput);
    });
    if (mqOverlay.addEventListener) mqOverlay.addEventListener('change', onMqChange);
    else if (mqOverlay.addListener) mqOverlay.addListener(onMqChange);

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(function () {
        scheduleLayout();
        revealMeasuredForm();
      });
      resizeObserver.observe(panel);
      resizeObserver.observe(hero);
      if (formEmbed) resizeObserver.observe(formEmbed);
      if (formIframe) resizeObserver.observe(formIframe);
    }

    if (formIframe && window.MutationObserver) {
      iframeMutationObserver = new MutationObserver(revealMeasuredForm);
      iframeMutationObserver.observe(formIframe, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVisualViewportResize);
    }

    function revealTypographyAndStartIntro() {
      /*
       * Measure the window labels with their final web font before exposing
       * them. Their transform depends on offsetWidth/offsetHeight, so revealing
       * them before this pass makes the clear label visibly jump when the font
       * finishes loading.
       */
      layout();
      trackRaf(function () {
        hero.classList.add('typography-ready');
        if (landing) landing.classList.add('typography-ready');
        trackRaf(intro);
      });
    }

    if (document.fonts && document.fonts.ready) {
      Promise.race([
        document.fonts.ready,
        new Promise(function (resolve) {
          trackTimeout(resolve, 800);
        }),
      ]).then(revealTypographyAndStartIntro);
    } else {
      revealTypographyAndStartIntro();
    }

    if (!formIframe) {
      hero.classList.add('form-ready');
    }

    syncMobileSheetVars();
    layout();
    hero.classList.add('geometry-ready');
    stage.classList.add('geometry-ready');
    if (mqOverlay.matches) finishIntroReveal();
    /*
     * Keep the reference mobile entrance, but animate only the compositor
     * transform. The form's reserved layout coordinates are already anchored
     * to the window's final resting position, so this motion cannot pull the
     * page geometry along with it.
     */
    if (!mqOverlay.matches && reduced) {
      revealMobileSheet();
    } else {
      trackTimeout(revealMobileSheet, 900);
    }
    trackRaf(function () {
      trackRaf(function () {
        hero.classList.add('ready');
      });
    });

    instances.set(stage, {
      destroy: function () {
        timers.forEach(function (id) {
          clearTimeout(id);
        });
        rafIds.forEach(function (id) {
          cancelAnimationFrame(id);
        });
        if (glide) cancelAnimationFrame(glide);
        grab.removeEventListener('pointerdown', onPointerDown);
        grab.removeEventListener('pointermove', onPointerMove);
        grab.removeEventListener('pointerup', onPointerUp);
        grab.removeEventListener('pointercancel', onPointerUp);
        hero.removeEventListener('click', onHeroClick);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onOrientationChange);
        window.removeEventListener('load', onWindowLoad);
        window.removeEventListener('message', onFormMessage);
        panel.removeEventListener('transitionend', onFormTransitionEnd);
        if (formIframe) formIframe.removeEventListener('load', onIframeLoad);
        if (nativeForm) nativeForm.removeEventListener('submit', onNativeFormSubmit);
        if (nativeEmailInput) nativeEmailInput.removeEventListener('input', onNativeEmailInput);
        nativeUrlInputs.forEach(function (input) {
          input.removeEventListener('input', onNativeUrlInput);
        });
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', onVisualViewportResize);
        }
        if (mqOverlay.removeEventListener) mqOverlay.removeEventListener('change', onMqChange);
        else if (mqOverlay.removeListener) mqOverlay.removeListener(onMqChange);
        if (resizeObserver) resizeObserver.disconnect();
        if (iframeMutationObserver) iframeMutationObserver.disconnect();
        stage.classList.remove('geometry-ready');
        instances.delete(stage);
      },
    });
  }

  function destroyStage(stage) {
    var inst = instances.get(stage);
    if (inst) inst.destroy();
  }

  function scan() {
    document.querySelectorAll('[data-rdv7-stage]').forEach(initStage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll('[data-rdv7-stage]').forEach(initStage);
    if (event.target.matches && event.target.matches('[data-rdv7-stage]')) {
      initStage(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll('[data-rdv7-stage]').forEach(destroyStage);
    if (event.target.matches && event.target.matches('[data-rdv7-stage]')) {
      destroyStage(event.target);
    }
  });
})();

(function () {
  'use strict';

  var runshowInstances = new WeakMap();

  function initRunshow(section) {
    if (runshowInstances.has(section)) return;

    var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-rdv7-runshow-tab]'));
    var panels = Array.prototype.slice.call(section.querySelectorAll('[data-rdv7-runshow-panel]'));
    var copyItems = Array.prototype.slice.call(section.querySelectorAll('[data-rdv7-runshow-copy]'));
    if (!tabs.length) return;

    function setActive(index) {
      tabs.forEach(function (tab) {
        var isActive = Number(tab.getAttribute('data-index')) === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', Number(panel.getAttribute('data-index')) === index);
      });

      copyItems.forEach(function (item) {
        item.classList.toggle('is-active', Number(item.getAttribute('data-index')) === index);
      });
    }

    function onTabEvent(event) {
      var tab = event.currentTarget;
      setActive(Number(tab.getAttribute('data-index')) || 0);
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', onTabEvent);
      tab.addEventListener('mouseenter', onTabEvent);
      tab.addEventListener('focus', onTabEvent);
    });

    runshowInstances.set(section, {
      destroy: function () {
        tabs.forEach(function (tab) {
          tab.removeEventListener('click', onTabEvent);
          tab.removeEventListener('mouseenter', onTabEvent);
          tab.removeEventListener('focus', onTabEvent);
        });
        runshowInstances.delete(section);
      },
    });
  }

  function destroyRunshow(section) {
    var instance = runshowInstances.get(section);
    if (instance) instance.destroy();
  }

  var revealObserver;

  function reveal(section) {
    section.classList.add('is-revealed');
    if (revealObserver) revealObserver.unobserve(section);
  }

  function observe(root) {
    var sections = [];
    if (root.matches && root.matches('[data-rdv7-reveal-section]')) sections.push(root);
    root.querySelectorAll('[data-rdv7-reveal-section]').forEach(function (section) {
      sections.push(section);
    });

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      sections.forEach(reveal);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      }, { threshold: 0.15 });
    }

    sections.forEach(function (section) {
      revealObserver.observe(section);
    });
  }

  function scanRunshow(root) {
    if (root.matches && root.matches('[data-rdv7-runshow]')) initRunshow(root);
    root.querySelectorAll('[data-rdv7-runshow]').forEach(initRunshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observe(document);
      scanRunshow(document);
    });
  } else {
    observe(document);
    scanRunshow(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    observe(event.target);
    scanRunshow(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;
    if (event.target.matches && event.target.matches('[data-rdv7-runshow]')) destroyRunshow(event.target);
    event.target.querySelectorAll('[data-rdv7-runshow]').forEach(destroyRunshow);
  });
})();

(function () {
  'use strict';

  var cleanupFns = [];
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function on(target, type, fn, opts) {
    if (!target) return;
    target.addEventListener(type, fn, opts);
    cleanupFns.push(function () { target.removeEventListener(type, fn, opts); });
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function setRunshowActive(index) {
    document.querySelectorAll('.rdv7-landing .rs-time').forEach(function (el) {
      el.classList.toggle('active', Number(el.getAttribute('data-idx')) === index);
    });
    document.querySelectorAll('.rdv7-landing .rs-frame').forEach(function (el) {
      el.classList.toggle('active', Number(el.getAttribute('data-idx')) === index);
    });
    document.querySelectorAll('.rdv7-landing .rs-copy-item').forEach(function (el) {
      el.classList.toggle('active', Number(el.getAttribute('data-idx')) === index);
    });
  }

  function initRunshowScroll() {
    var rsSection = document.getElementById('runofshow');
    var rsOuter = document.getElementById('rsOuter');
    if (!rsSection || !rsOuter) return;

    var mqRsMobile = window.matchMedia('(max-width:880px)');
    var rsIdx = -1;
    var RS_STEP = 560;

    function setActive(index) {
      if (index === rsIdx) return;
      rsIdx = index;
      setRunshowActive(index);
    }

    function sizeRsOuter() {
      if (mqRsMobile.matches) {
        rsOuter.style.height = '';
        return;
      }
      rsOuter.style.height = (rsSection.offsetHeight + RS_STEP * 3) + 'px';
    }

    function updateRsProgress() {
      if (mqRsMobile.matches) return;
      var runway = rsOuter.offsetHeight - rsSection.offsetHeight;
      if (runway <= 0) return;
      var scrolled = -rsOuter.getBoundingClientRect().top;
      var progress = clamp(scrolled / runway, 0, 1);
      setActive(Math.min(3, Math.floor(progress * 4)));
    }

    sizeRsOuter();
    updateRsProgress();
    on(window, 'scroll', updateRsProgress, { passive: true });
    on(window, 'resize', function () {
      sizeRsOuter();
      updateRsProgress();
    }, { passive: true });
    on(mqRsMobile, 'change', function () {
      sizeRsOuter();
      updateRsProgress();
    });
  }

  function initRunshowMobile() {
    var rsMobileTrack = document.getElementById('rsMobileTrack');
    if (!rsMobileTrack) return;

    var mqRsAuto = window.matchMedia('(max-width:880px)');
    var rsDots = Array.prototype.slice.call(document.querySelectorAll('.rdv7-landing .rs-dot'));
    var rsTabs = Array.prototype.slice.call(document.querySelectorAll('.rdv7-landing .rs-tab'));
    var rsMobileItems = Array.prototype.slice.call(document.querySelectorAll('.rdv7-landing .rs-mobile-item'));
    var rsCurrent = 0;
    var rsUserActive = false;
    var rsAutoTimer = null;
    var rsResumeTimer = null;

    function syncRsTrackHeight(index) {
      var item = rsMobileItems[index];
      if (!item || !mqRsAuto.matches) {
        rsMobileTrack.style.height = '';
        return;
      }
      var styles = window.getComputedStyle(rsMobileTrack);
      var verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      rsMobileTrack.style.height = (item.offsetHeight + verticalPadding) + 'px';
    }

    function updateRsDots() {
      if (!rsMobileItems.length) return;
      var closest = 0;
      var min = Infinity;
      rsMobileItems.forEach(function (item, index) {
        var distance = Math.abs(item.offsetLeft - rsMobileTrack.scrollLeft);
        if (distance < min) {
          min = distance;
          closest = index;
        }
      });
      rsCurrent = closest;
      syncRsTrackHeight(closest);
      rsDots.forEach(function (dot, index) {
        var isActive = index === closest;
        dot.classList.toggle('active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
      rsTabs.forEach(function (tab, index) { tab.classList.toggle('active', index === closest); });
    }

    function goToRsMobile(index) {
      if (!rsMobileItems.length) return;
      rsCurrent = ((index % rsMobileItems.length) + rsMobileItems.length) % rsMobileItems.length;
      rsDots.forEach(function (dot, dotIndex) {
        var isActive = dotIndex === rsCurrent;
        dot.classList.toggle('active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
      rsTabs.forEach(function (tab, tabIndex) {
        tab.classList.toggle('active', tabIndex === rsCurrent);
      });
      syncRsTrackHeight(rsCurrent);
      rsMobileTrack.scrollTo({ left: rsMobileItems[rsCurrent].offsetLeft, behavior: 'smooth' });
    }

    function stopRsAuto() {
      if (rsAutoTimer) clearInterval(rsAutoTimer);
      rsAutoTimer = null;
    }

    function startRsAuto() {
      stopRsAuto();
      if (reduced.matches || !mqRsAuto.matches) return;
      rsAutoTimer = setInterval(function () {
        if (!rsUserActive && mqRsAuto.matches) goToRsMobile(rsCurrent + 1);
      }, 4000);
    }

    function pauseRsAuto() {
      rsUserActive = true;
      if (rsResumeTimer) clearTimeout(rsResumeTimer);
    }

    function scheduleRsResume() {
      if (rsResumeTimer) clearTimeout(rsResumeTimer);
      rsResumeTimer = setTimeout(function () { rsUserActive = false; }, 2500);
    }

    on(rsMobileTrack, 'scroll', function () { window.requestAnimationFrame(updateRsDots); scheduleRsResume(); }, { passive: true });
    rsTabs.forEach(function (tab, index) {
      on(tab, 'click', function () {
        pauseRsAuto();
        goToRsMobile(index);
        scheduleRsResume();
      });
    });
    rsDots.forEach(function (dot, index) {
      on(dot, 'click', function () {
        pauseRsAuto();
        goToRsMobile(index);
        scheduleRsResume();
      });
    });
    on(rsMobileTrack, 'pointerdown', pauseRsAuto);
    on(rsMobileTrack, 'touchstart', pauseRsAuto, { passive: true });
    ['pointerup', 'touchend', 'pointercancel'].forEach(function (eventName) {
      on(rsMobileTrack, eventName, scheduleRsResume);
    });
    on(mqRsAuto, 'change', function () {
      syncRsTrackHeight(rsCurrent);
      startRsAuto();
    });
    on(window, 'resize', function () { syncRsTrackHeight(rsCurrent); }, { passive: true });
    cleanupFns.push(stopRsAuto);
    updateRsDots();
    startRsAuto();
  }

  function initConversationEntrance() {
    var convoSection = document.getElementById('conversations-panels');
    var convoOuter = document.getElementById('convoOuter');
    if (!convoSection || !convoOuter) return;

    var convoPanelEls = Array.prototype.slice.call(convoSection.querySelectorAll('.convo-panel'));
    var convoMobile = window.matchMedia('(max-width:880px)');

    function convoStage() {
      return clamp(window.innerWidth * 0.3, 220, 480);
    }

    function sizeConvoOuter() {
      convoOuter.style.setProperty('--convo-entrance', convoStage() + 'px');
    }

    function updateConvoEntrance() {
      if (convoMobile.matches) {
        var mobileTop = convoOuter.getBoundingClientRect().top;
        var mobileStart = window.innerHeight * 0.85;
        var mobileProgress = clamp((mobileStart - mobileTop) / mobileStart, 0, 1);
        var mobileOffset = clamp(window.innerWidth * 0.16, 80, 260);
        convoPanelEls.forEach(function (el) {
          el.style.transform = 'translateX(' + (mobileOffset * (1 - mobileProgress)) + 'px)';
          el.style.opacity = String(mobileProgress);
        });
        return;
      }

      if (reduced.matches) {
        convoPanelEls.forEach(function (el) {
          el.style.transform = 'none';
          el.style.opacity = '1';
        });
        return;
      }

      var stage = convoStage();
      var off = clamp(window.innerWidth * 0.16, 80, 260);
      var scrolled = Math.max(0, -convoOuter.getBoundingClientRect().top);
      var progress = clamp(scrolled / stage, 0, 1);
      convoPanelEls.forEach(function (el) {
        el.style.transform = 'translateX(' + (off * (1 - progress)) + 'px)';
        el.style.opacity = String(progress);
      });
    }

    sizeConvoOuter();
    updateConvoEntrance();
    on(window, 'scroll', updateConvoEntrance, { passive: true });
    on(window, 'resize', function () {
      sizeConvoOuter();
      updateConvoEntrance();
    }, { passive: true });
    on(convoMobile, 'change', updateConvoEntrance);
  }

  function initStageTransitionEntrance() {
    var stageOuter = document.getElementById('stageOuter');
    var stageInner = document.getElementById('stageTransitionInner');
    if (!stageOuter || !stageInner) return;

    var stageMobile = window.matchMedia('(max-width:880px)');

    function updateStageTransitionEntrance() {
      if (!stageMobile.matches || reduced.matches) {
        stageInner.style.filter = '';
        stageInner.style.opacity = '';
        stageInner.style.transform = '';
        return;
      }

      var top = stageInner.getBoundingClientRect().top;
      var start = window.innerHeight * 0.92;
      var end = window.innerHeight * 0.42;
      var progress = clamp((start - top) / (start - end), 0, 1);
      var eased = 1 - Math.pow(1 - progress, 3);

      stageInner.style.filter = 'blur(' + (8 * (1 - eased)).toFixed(2) + 'px)';
      stageInner.style.opacity = String(eased);
      stageInner.style.transform = 'translateY(' + (28 * (1 - eased)).toFixed(2) + 'px) scale(' + (0.96 + 0.04 * eased).toFixed(4) + ')';
    }

    updateStageTransitionEntrance();
    on(window, 'scroll', updateStageTransitionEntrance, { passive: true });
    on(window, 'resize', updateStageTransitionEntrance, { passive: true });
    on(stageMobile, 'change', updateStageTransitionEntrance);
  }

  function initAskExpertEntrance() {
    var aePanels = document.getElementById('aePanels');
    if (!aePanels) return;

    var aePanelEls = Array.prototype.slice.call(aePanels.querySelectorAll('.convo-panel'));

    function updateAeEntrance() {
      if (reduced.matches) {
        aePanelEls.forEach(function (el) {
          el.style.transform = 'none';
          el.style.opacity = '1';
        });
        return;
      }

      var rect = aePanels.getBoundingClientRect();
      var start = window.innerHeight * 0.88;
      var end = window.innerHeight * 0.45;
      var progress = clamp((start - rect.top) / (start - end), 0, 1);
      var off = clamp(window.innerWidth * 0.16, 80, 260);
      aePanelEls.forEach(function (el) {
        el.style.transform = 'translateX(' + (off * (1 - progress)) + 'px)';
        el.style.opacity = String(progress);
      });
    }

    updateAeEntrance();
    on(window, 'scroll', updateAeEntrance, { passive: true });
    on(window, 'resize', updateAeEntrance, { passive: true });
  }

  function initReferenceSections() {
    cleanupFns.forEach(function (fn) { fn(); });
    cleanupFns = [];
    initRunshowScroll();
    initRunshowMobile();
    initConversationEntrance();
    initStageTransitionEntrance();
    initAskExpertEntrance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReferenceSections);
  } else {
    initReferenceSections();
  }

  document.addEventListener('shopify:section:load', initReferenceSections);
  document.addEventListener('shopify:section:unload', function () {
    cleanupFns.forEach(function (fn) { fn(); });
    cleanupFns = [];
  });
})();
